import React from 'react';
import styled from 'styled-components';

import { usePerx3Redesign } from 'hooks';

import './fonts';
import { IntelligenceThemeVariant, intelligenceTokenDeclarationsFor } from './tokens';
import useIntelligenceScale from './useIntelligenceScale';

export interface IIntelligenceThemeScopeProps {
  children?: React.ReactNode;
  className?: string;
  /**
   * PLA-8844: measures this element and keeps `--is-scale` on it, so every scaled token below
   * follows the CONTENT COLUMN rather than the viewport. Opt-in because the scope is also mounted
   * on surfaces that have no column of their own - Orbit's chart wrapper is one - which must stay
   * at the drawn size. `IntelligenceContentScope` turns it on for the two routes when the
   * `intelligence_responsive_layout` flag is on.
   */
  responsiveScale?: boolean;
}

const ThemeScopeStyled = styled.div<{ $variant: IntelligenceThemeVariant }>`
  ${({ $variant }) => intelligenceTokenDeclarationsFor($variant)}

  /* PLA-8844: a flex item's default lower bound is its own content, so without this the column
     refuses to narrow past what the widest card wants and the measured width - and therefore the
     scale - never moves. Harmless outside a flex parent. */
  min-width: 0;
`;

/**
 * Declares the Intelligence Suite design tokens on one element so every descendant inherits them.
 * It carries no visual rules of its own - surfaces and text colours belong to the route's content
 * scope, which composes this wrapper.
 *
 * `perx_3_redesign` swaps the palette here rather than at each consumer: the two variants share one
 * key set, so the 40+ `tokens.color.X` readers resolve the light values with no change of their own.
 *
 * Inheritance is DOM-based, so an antd overlay portalled into document.body (Modal, Drawer, Select
 * and DatePicker popups, Tooltip) escapes it and resolves every var(--is-*) to nothing. When P2 adds
 * such an overlay it has to pass `getPopupContainer` pointing back at this element.
 */
const IntelligenceThemeScope: React.FC<IIntelligenceThemeScopeProps> = ({
  children,
  className,
  responsiveScale = false
}) => {
  const isPerx3 = usePerx3Redesign();
  const scopeRef = React.useRef<HTMLDivElement>(null);

  useIntelligenceScale(scopeRef, responsiveScale);

  return (
    <ThemeScopeStyled
      ref={scopeRef}
      $variant={isPerx3 ? 'light' : 'dark'}
      className={className}
      data-testid="intelligence-theme-scope"
    >
      {children}
    </ThemeScopeStyled>
  );
};

export default IntelligenceThemeScope;
